import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import PageSizeStation from '../../../shared/component/PageSizeStation';
import { LIMIT_PAGE_NUM } from '../../../shared/constants/limits';
import { PerPage } from '../../../shared/enum/EnumRepository';
import { useAdminMaterialAlias } from '../../share/hook/useAdminMaterialAlias';

const PageAboutMaterialAlias: React.FC = () => {
  const {
    page,
    totalPages,
    pageSize,
    unassignedOnly,
    setPageSize,
    load,
  } = useAdminMaterialAlias();
  const [currentWindow, setCurrentWindow] = useState(1);

  const startPage = (currentWindow - 1) * LIMIT_PAGE_NUM + 1;
  const endPage = Math.min(startPage + LIMIT_PAGE_NUM - 1, totalPages);

  useEffect(() => {
    const windowForCurrentPage = Math.max(
      1,
      Math.ceil(page / LIMIT_PAGE_NUM),
    );
    if (windowForCurrentPage !== currentWindow) {
      setCurrentWindow(windowForCurrentPage);
    }
  }, [page]);

  const handlePageClick = (pageNumber: number) => {
    load(pageNumber, unassignedOnly, Number(pageSize));
  };

  const handlePageWindowClick = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentWindow > 1) {
      const nextWindow = currentWindow - 1;
      const nextPage = (nextWindow - 1) * LIMIT_PAGE_NUM + 1;
      setCurrentWindow(nextWindow);
      handlePageClick(nextPage);
    } else if (
      direction === 'next'
      && currentWindow < Math.ceil(totalPages / LIMIT_PAGE_NUM)
    ) {
      const nextWindow = currentWindow + 1;
      const nextPage = (nextWindow - 1) * LIMIT_PAGE_NUM + 1;
      setCurrentWindow(nextWindow);
      handlePageClick(nextPage);
    }
  };

  return (
    <PageContainer>
      <PageButtonContainer>
        {currentWindow > 1 && (
          <PageWindowButton onClick={() => handlePageWindowClick('prev')}>
            ←
          </PageWindowButton>
        )}
        {Array.from(
          { length: Math.max(0, endPage - startPage + 1) },
          (_, index) => startPage + index,
        ).map((pageNumber) => (
          pageNumber === page ? (
            <CurrPageButton key={pageNumber}>{pageNumber}</CurrPageButton>
          ) : (
            <PageButton
              key={pageNumber}
              onClick={() => handlePageClick(pageNumber)}
            >
              {pageNumber}
            </PageButton>
          )
        ))}
        {currentWindow < Math.ceil(totalPages / LIMIT_PAGE_NUM) && (
          <PageWindowButton onClick={() => handlePageWindowClick('next')}>
            →
          </PageWindowButton>
        )}
      </PageButtonContainer>
      <PageSizeStationWrapper>
        <PageSizeStation
          currentValue={pageSize}
          handlePerPageChange={(value: PerPage) => setPageSize(value)}
          direction={true}
        />
      </PageSizeStationWrapper>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  width: 100%;
  height: calc(3.5vh);
  max-height: 30px;
  display: flex;
  align-items: center;
  gap: 30px;
  overflow: visible;
  ${({ theme }) => theme.typography.Body_04};
`;

const PageButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageButton = styled.div`
  width: fit-content;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 2px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  transition: all 0.2s ease-in;

  &:hover {
    transform: scale(1.1);
    color: ${({ theme }) => theme.colors.color_Gray_04};
  }
`;

const CurrPageButton = styled(PageButton)`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  cursor: default;
`;

const PageWindowButton = styled(PageButton)``;

const PageSizeStationWrapper = styled.div`
  overflow: visible;
  position: relative;
  z-index: 4;
`;

export default PageAboutMaterialAlias;
